import { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import type { Supplier, CreateSupplierDTO, UpdateSupplierDTO, SupplierCategory } from '@yawi/types';
import { useI18n } from '@/shared/i18n';
import { useFormValidation } from '@/shared/hooks/useFormValidation';

interface SupplierFormProps {
  supplier?: Supplier;
  onSubmit: (data: CreateSupplierDTO | UpdateSupplierDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  submissionError?: unknown;
}

const CATEGORIES: SupplierCategory[] = [
  'PLOMERIA',
  'ELECTRICIDAD',
  'JARDINERIA',
  'LIMPIEZA',
  'MANTENIMIENTO',
  'SEGURIDAD',
  'FUMIGACION',
  'MUDANZAS',
  'REPARACIONES',
  'CERRAJERIA',
  'PINTURA',
  'CARPINTERIA',
  'AIRE_ACONDICIONADO',
  'ALBERCAS',
  'IMPERMEABILIZACION',
  'OTRO',
];

export const SupplierForm = ({ supplier, onSubmit, onCancel, loading, submissionError }: SupplierFormProps) => {
  const { t } = useI18n();
  const isEditing = !!supplier;
  const { getFieldError, clearFieldError } = useFormValidation({ submissionError });

  const [formData, setFormData] = useState<CreateSupplierDTO>({
    name: '',
    category: 'OTRO',
    description: '',
    contactName: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        category: supplier.category,
        description: supplier.description || '',
        contactName: supplier.contactName || '',
        phone: supplier.contactPhone || '',
        email: supplier.contactEmail || '',
        address: supplier.address || '',
        notes: supplier.notes || '',
      });
    }
  }, [supplier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    await onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {isEditing ? t('suppliers.edit') : t('suppliers.create')}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.name')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
                clearFieldError('name');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                getFieldError('name') ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
              required
              maxLength={255}
            />
            {getFieldError('name') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('name')}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('suppliers.category.label')} *
            </label>
            <select
              value={formData.category}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, category: e.target.value as SupplierCategory }));
                clearFieldError('category');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                getFieldError('category') ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
              required
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {t(`suppliers.category.${cat.toLowerCase()}`)}
                </option>
              ))}
            </select>
            {getFieldError('category') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('category')}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.description')}
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, description: e.target.value }));
                clearFieldError('description');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${
                getFieldError('description') ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
              rows={2}
              maxLength={1000}
            />
            {getFieldError('description') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('description')}</p>
            )}
          </div>

          {/* Contact Info */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">{t('suppliers.contactInfo')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  {t('suppliers.contactName')}
                </label>
                <input
                  type="text"
                  value={formData.contactName || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, contactName: e.target.value }));
                    clearFieldError('contactName');
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    getFieldError('contactName') ? 'border-red-300 bg-red-50' : 'border-slate-300'
                  }`}
                  maxLength={255}
                />
                {getFieldError('contactName') && (
                  <p className="mt-1 text-xs text-red-500">{getFieldError('contactName')}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    {t('common.phone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, phone: e.target.value }));
                      clearFieldError('phone');
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      getFieldError('phone') ? 'border-red-300 bg-red-50' : 'border-slate-300'
                    }`}
                    maxLength={20}
                    minLength={10}
                  />
                  {getFieldError('phone') && (
                    <p className="mt-1 text-xs text-red-500">{getFieldError('phone')}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    {t('common.email')}
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, email: e.target.value }));
                      clearFieldError('email');
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      getFieldError('email') ? 'border-red-300 bg-red-50' : 'border-slate-300'
                    }`}
                  />
                  {getFieldError('email') && (
                    <p className="mt-1 text-xs text-red-500">{getFieldError('email')}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  {t('common.address')}
                </label>
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, address: e.target.value }));
                    clearFieldError('address');
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    getFieldError('address') ? 'border-red-300 bg-red-50' : 'border-slate-300'
                  }`}
                  maxLength={500}
                />
                {getFieldError('address') && (
                  <p className="mt-1 text-xs text-red-500">{getFieldError('address')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.notes')}
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, notes: e.target.value }));
                clearFieldError('notes');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none ${
                getFieldError('notes') ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
              rows={2}
              maxLength={2000}
            />
            {getFieldError('notes') && (
              <p className="mt-1 text-xs text-red-500">{getFieldError('notes')}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
